/**
 * Generic Confirmation Modal
 * Supports different variants: danger, warning, info
 */
import { motion, AnimatePresence } from 'framer-motion'
import { Trash2, X, Loader2, AlertTriangle, Info } from 'lucide-react'

type ModalVariant = 'danger' | 'warning' | 'info'

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: ModalVariant
}

const variantConfig = {
  danger: {
    icon: Trash2,
    iconBg: 'bg-destructive-soft',
    iconColor: 'text-destructive-foreground',
    buttonBg: 'bg-destructive hover:bg-destructive/90',
    buttonText: 'text-white'
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-warning-soft',
    iconColor: 'text-warning-foreground',
    buttonBg: 'bg-warning hover:bg-warning/90',
    buttonText: 'text-white'
  },
  info: {
    icon: Info,
    iconBg: 'bg-info-soft',
    iconColor: 'text-info-foreground',
    buttonBg: 'bg-info hover:bg-info/90',
    buttonText: 'text-white'
  }
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger'
}: ConfirmModalProps) {
  const config = variantConfig[variant]
  const Icon = config.icon

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-subtle">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${config.iconBg} rounded-full flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${config.iconColor}`} />
                </div>
                <h2 className="text-xl font-semibold text-foreground">{title}</h2>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="p-2 hover:bg-foreground/[0.06] rounded-lg transition-colors disabled:text-disabled-foreground disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-muted-foreground">
                {message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 p-6 bg-foreground/[0.04] border-t border-subtle">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-foreground font-medium rounded-lg hover:bg-foreground/[0.10] transition-colors disabled:text-disabled-foreground disabled:cursor-not-allowed"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`px-4 py-2 ${config.buttonBg} ${config.buttonText} font-medium rounded-lg transition-colors disabled:bg-disabled disabled:text-disabled-foreground disabled:cursor-not-allowed flex items-center gap-2`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Icon className="w-4 h-4" />
                    {confirmText}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
